/**
 * CapacityScopeChips — the SLOT a capacity rule caps, radio-style:
 * "Default paths" (the flow's default bucket) · "Rule paths" (each rule group
 * individually) · Landers/Offers ("· soon" while the launch gates them).
 * One slot per rule; slots capped by other rules are locked.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { CapacitySlot } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import {
  AVAILABLE_SLOTS,
  COMING_SOON_SLOTS,
  SLOT_LABEL,
} from './capacity-rules';

export interface CapacityScopeChipsProps {
  value: CapacitySlot;
  /** Slots already capped by OTHER rules — locked here. */
  disabledSlots?: ReadonlyArray<CapacitySlot>;
  onChange: (slot: CapacitySlot) => void;
}

const CapacityScopeChips: FC<CapacityScopeChipsProps> = ({
  value,
  disabledSlots = [],
  onChange,
}) => {
  const locked = new Set(disabledSlots);
  const chips: CapacitySlot[] = [...AVAILABLE_SLOTS, ...COMING_SOON_SLOTS];
  return (
    <div className="inline-flex flex-wrap items-center gap-1" role="radiogroup">
      {chips.map((slot) => {
        const soon = COMING_SOON_SLOTS.includes(slot);
        const disabled = soon || (locked.has(slot) && slot !== value);
        const selected = slot === value;
        return (
          <button
            key={slot}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => !disabled && onChange(slot)}
            title={
              soon
                ? 'Coming soon — the launch focuses capacity on paths'
                : locked.has(slot) && !selected
                  ? 'Already capped by another rule'
                  : SLOT_LABEL[slot]
            }
            className={cn(
              'inline-flex h-6 items-center rounded-full border px-2 text-[10.5px] font-medium transition-colors',
              selected
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground',
              !disabled && !selected && 'hover:border-primary/40 hover:text-foreground',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {SLOT_LABEL[slot]}
            {soon ? ' · soon' : ''}
          </button>
        );
      })}
    </div>
  );
};

export default CapacityScopeChips;
